import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './AnalyzeModal.css';
import { generateDisplayName } from '../../utils/nameGenerator';
import ModelSelect from './ModelSelect';

const AnalyzeModal = ({ isOpen, onClose, onAnalyze, ticketKey, existingNames = [], project = null }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [name, setName] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [model, setModel] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Load model from project settings when modal opens
  useEffect(() => {
    if (isOpen) {
      if (!name) {
        const generatedName = generateDisplayName('analysis', existingNames);
        setName(generatedName);
      }
      
      // Load AI model from project settings
      if (project?.aiModel) {
        setModel(project.aiModel);
      } else {
        setModel(''); // no house default — the picker opens unselected
      }
    }
  }, [isOpen, existingNames, name, project]);

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    try {
      await onAnalyze(name, additionalContext, model);
      // Modal will be closed by parent after navigation
    } catch (error) {
      console.error('Error starting analysis:', error);
      setIsAnalyzing(false);
    }
  };

  const handleClose = () => {
    if (!isAnalyzing) {
      setName('');
      setAdditionalContext('');
      setModel('');
      onClose();
    }
  };

  if (!isOpen) return null;

  const aiAgent = project?.aiAgent || 'claude';
  const hasApiKey = !!project?.secrets?.aiApiKey;
  const keyLabel = aiAgent === 'claude' ? 'Claude API' : 'API';

  return (
    <div className="analyze-modal-overlay" onClick={handleClose}>
      <div className="analyze-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Analyze Ticket {ticketKey}</h2>
          <button className="close-btn" onClick={handleClose} disabled={isAnalyzing}>
            ×
          </button>
        </div>
        
        <div className="modal-content">
          {!hasApiKey && (
            <div className="analyze-api-key-alert">
              <span className="alert-icon">&#9888;</span>
              <div>
                <strong>{keyLabel} key is not configured.</strong>
                {' '}An API key is required to run analysis.{' '}
                <a
                  href={`/projects/${projectId}/settings?section=ai`}
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                    navigate(`/projects/${projectId}/settings?section=ai`);
                  }}
                >
                  Go to AI Settings
                </a>
              </div>
            </div>
          )}

          <div className="analyze-input-section">
            <label htmlFor="execution-name">
              ID
            </label>
            <input
              id="execution-name"
              type="text"
              value={name}
              readOnly
              style={{ opacity: 0.7, cursor: 'default' }}
            />
          </div>

          <div className="analyze-input-section" style={{ marginTop: '16px' }}>
            <label htmlFor="additional-context">
              Additional Context (Optional)
            </label>
            <textarea
              id="additional-context"
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="Add any specific questions, concerns, or context you'd like the analysis to address..."
              rows={4}
              disabled={isAnalyzing}
            />
          </div>

          <div className="analyze-input-section" style={{ marginTop: '16px' }}>
            <ModelSelect
              aiAgent={aiAgent}
              value={model}
              onChange={setModel}
              disabled={isAnalyzing}
              label={`AI Model ${aiAgent === 'claude' ? '(Claude API)' : ''}`}
            />
          </div>

          <button
            className="analyze-btn"
            onClick={handleAnalyze}
            disabled={isAnalyzing || !name.trim() || !hasApiKey || !model}
          >
            {isAnalyzing ? 'Starting Analysis...' : 'Start Analysis'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyzeModal;
